/**
 * **联机汇合页** —— 一个房间号输入框 + 确认加入 + 创建房间。
 *
 * 评审：「联机汇合页则只有一个房间号输入框，一个确认加入键，和一个创建房间键。」
 * 所以这一屏刻意**只有这三件东西**，昵称之类的都归设置页 ——
 * 需要改昵称的话，右边那栏会告诉你当前叫什么、去哪儿改。
 *
 * ## 房间号是"念给朋友听"的东西，这决定了它的所有细节
 *
 * - 字母表**剔掉了 `I` `L` `O` `0` `1`**（`room.ts` 的 `ROOM_CODE_ALPHABET`）——
 *   手写和口述时这几个必然混淆
 * - 输入一律**转大写**（`transform`），所以大小写不会成为"为什么加不进去"的原因
 * - 输入非法字符时**直接不收**，而不是打完再报错
 *
 * ## 现在是真联机
 *
 * 两个按钮都会**真的**去连（探信令中继 → 进房），并且都不直接进房间页 ——
 * 先进"正在连接"页（`connecting.ts`），连上了才换过去，连不上就退回来。
 * 这一页上那句"传输层尚未接入"的旧提示随这一轮删掉了：
 * **已经过时的提示比没有提示更糟**，它说的正好是反的。
 */

import { Container } from 'pixi.js';
import {
  STAMP_GAP,
  banner,
  createColumn,
  infoRow,
  richLabel,
  pageChrome,
  panel,
  stampButton,
  textField,
  uiPad,
  INK,
  type TextField,
} from '../../render/ui';
import { displayNickname } from '../settings';
import { bi, t } from '../../i18n';
import {
  ROOM_CODE_ALPHABET,
  ROOM_CODE_LENGTH,
  generateRoomCode,
  isValidRoomCode,
  normalizeRoomCode,
} from '../room';
import type { Screen, ScreenContext } from '../screen';
import { OVERLAY, SCREEN } from './ids';

const PANEL_X = 96;
const PANEL_WIDTH = 560;
const SIDE_X = 720;
const SIDE_WIDTH = 464;

/** 房间号只收字母表里那几个字符 */
const acceptsRoomCodeChar = (ch: string): boolean => ROOM_CODE_ALPHABET.includes(ch);

export function createMeetupScreen(ctx: ScreenContext): Screen {
  const container = new Container();
  container.label = 'screen-meetup';
  let column = createColumn(1);
  let field: TextField | null = null;

  const build = (): void => {
    const settings = ctx.settings.get();
    const scale = settings.uiScale;
    const { width, height } = ctx.bounds;

    column = createColumn(scale);
    container.addChild(column.container);
    column.addRaw(
      pageChrome({ scale, width, height, header: bi('common.netplay'), page: 'P.02' }),
    );

    const title = banner({
      text: 'NETPLAY',
      scale,
      width: 640,
      sub: t('common.netplay'),
    });
    title.container.position.set(uiPad(PANEL_X, scale), uiPad(140, scale));
    column.addRaw(title.container);

    // ---------------- 左边的操作面板 ----------------
    const sheet = panel({ scale, width: PANEL_WIDTH, title: bi('common.room'), padding: 20 });
    sheet.container.position.set(uiPad(PANEL_X, scale), uiPad(272, scale));
    column.addRaw(sheet.container);

    const inner = createColumn(scale, 14);
    sheet.body.addChild(inner.container);
    const contentWidth = PANEL_WIDTH - uiPad(40, scale);

    inner.add(
      richLabel({
        text: t('meetup.roomCode', { n: ROOM_CODE_LENGTH }),
        scale,
        size: 13,
        fill: INK.textDim,
        role: 'cjk',
      }),
      0,
      uiPad(2, scale),
    );

    // 先造出来才能互相引用（输入框变化要启用/禁用"加入"）
    const joinButton = stampButton({
      label: t('meetup.join'),
      scale,
      variant: 'primary',
      onPress: () => join(),
    });
    const createButton = stampButton({
      label: t('common.createRoom'),
      scale,
      variant: 'secondary',
      onPress: () => create(),
    });

    field = textField({
      scale,
      width: uiPad(240, scale),
      keys: ctx.keys,
      maxLength: ROOM_CODE_LENGTH,
      placeholder: 'ABCD',
      // 框前显示一个 #；输入逻辑不变（# 本来就被 accepts 滤掉，不进 buffer）
      prefix: '#',
      accepts: acceptsRoomCodeChar,
      // 大小写不该成为"为什么加不进去"的原因，直接转大写
      transform: (value) => value.toUpperCase(),
      // ⚠️ **整条分享链接粘进来要认出来**：默认粘贴是逐字符过滤，会把
      //    `https://…` 吃成 `HTTP`（H/T/T/P 正好在字母表里）。这里接管粘贴，
      //    先用 normalizeRoomCode 摘 `#` 后面的房间号，再整段设进去。
      onPaste: (text) => field?.setValue(normalizeRoomCode(text)),
      onChange: (value) => joinButton.setEnabled(isValidRoomCode(value)),
      onSubmit: () => join(),
      onCancel: () => ctx.stack.pop(),
      onTab: () => join(),
    });
    joinButton.setEnabled(false);
    inner.add(field);
    inner.track(() => field?.blur());

    const actions = new Container();
    createButton.container.position.set(joinButton.width + uiPad(STAMP_GAP, scale), 0);
    actions.addChild(joinButton.container, createButton.container);
    inner.add({ container: actions, width: contentWidth, height: joinButton.height });

    const back = stampButton({
      label: t('common.back'),
      scale,
      variant: 'quiet',
      onPress: () => ctx.stack.pop(),
    });
    inner.add(back);

    sheet.fitContent(Math.round(inner.y) - uiPad(14, scale));

    // ---------------- 右边的身份栏 ----------------
    const side = panel({ scale, width: SIDE_WIDTH, title: bi('common.identity'), padding: 18 });
    side.container.position.set(uiPad(SIDE_X, scale), uiPad(272, scale));
    column.addRaw(side.container);

    const sideInner = createColumn(scale, 10);
    side.body.addChild(sideInner.container);
    const sideContentWidth = SIDE_WIDTH - uiPad(36, scale);

    sideInner.add(
      infoRow({
        label: t('common.nickname'),
        value: displayNickname(settings),
        scale,
        width: sideContentWidth,
        valueFill: INK.accent,
      }),
    );

    const settingsButton = stampButton({
      label: t('meetup.changeNickname'),
      scale,
      variant: 'secondary',
      onPress: () => ctx.stack.openOverlay(OVERLAY.settings),
    });
    sideInner.add(settingsButton);
    side.fitContent(Math.round(sideInner.y) - uiPad(10, scale));
  };

  /**
   * 生成一个房间号，然后**去连**。
   *
   * ⚠️ 这里推的是**"正在连接"页**，不是房间页。评审：
   *
   * > 应当加一个"正在连接"页面，无论是正在加入游戏还是正在创建房间都进入
   * > 这个界面，若出现网络问题就返回之前的页面。
   *
   * 房间页只在连接真的成功之后才进（那一页自己负责换过去）。
   * 所以这两个函数里**没有** `ctx.stack.push(SCREEN.room)`。
   */
  const create = (): void => {
    const settings = ctx.settings.get();
    ctx.room.createRoom({
      // 没有服务端分配房号，就用时间当种子。可复现不是这里的目标，
      // 目标是"每次点都不一样"
      code: generateRoomCode(Date.now()),
      selfId: ctx.selfId,
      nickname: displayNickname(settings),
    });
    ctx.stack.push(SCREEN.connecting);
  };

  /** 用输入框里的房号去连 */
  const join = (): void => {
    const code = field?.value ?? '';
    if (!isValidRoomCode(code)) return;
    const settings = ctx.settings.get();
    ctx.room.joinRoom({ code, selfId: ctx.selfId, nickname: displayNickname(settings) });
    ctx.stack.push(SCREEN.connecting);
  };

  build();

  return {
    container,
    onEnter() {
      // 进这一屏就聚焦输入框 —— 这是这一页唯一要做的事。
      // ⚠️ 焦点会往按键路由上压一个处理者，而宿主管这个顺序（见 screenHost 的说明）
      field?.focus();
    },
    onKey(event) {
      if (event.key !== 'Escape') return false;
      // 输入框自己会先处理 ESC（它会把焦点还回来），到这儿说明没焦点
      ctx.stack.pop();
      return true;
    },
    rebuild() {
      // 重建之后**不在这里抢焦点**：宿主重建完会重新走一遍 onEnter，
      // 焦点的时机由那一处统一管，免得两条路各抢一次、顺序还不同
      column.dispose();
      container.removeChildren();
      field = null;
      build();
    },
    destroy() {
      column.dispose();
      container.destroy({ children: true });
    },
  };
}
